/**
 * Whether the national eID middleware can serve a card read on this workstation.
 *
 * A third, independent question about the chip path. The capability list answers "does this build
 * implement chip reading"; the reader inventory answers "is a reader plugged in"; this answers
 * "can the workstation actually talk to the card". A counter can satisfy the first two and still
 * fail the third, which is how an operator came to be asked for a PIN for a read that could never
 * succeed.
 *
 * The middleware is an external prerequisite distributed by the Romanian Ministry of Internal
 * Affairs. Nothing here downloads, starts, bundles or mirrors it — this module only reads what the
 * workstation service reports.
 */

/** The states the service publishes. A closed set; anything else is not understood. */
export type EidMiddlewareState = 'ready' | 'unavailable' | 'unknown' | 'not-required';

/** One readiness verdict, exactly as the service reports it. */
export interface EidMiddlewareReadiness {
    readonly state: EidMiddlewareState;
    /** A bounded service code. Never rendered raw — it selects a sentence. */
    readonly code: string;
}

/** The states this page accepts. Anything outside the set is treated as not understood. */
const STATES: readonly EidMiddlewareState[] = ['ready', 'unavailable', 'unknown', 'not-required'];

/**
 * The blocking codes, each of which needs a different sentence because each needs a different
 * action from whoever reads it.
 */
export const MIDDLEWARE_NOT_INSTALLED = 'MIDDLEWARE_NOT_INSTALLED';
export const MIDDLEWARE_NOT_RUNNING = 'MIDDLEWARE_NOT_RUNNING';
export const MIDDLEWARE_UNTRUSTED_PROCESS = 'MIDDLEWARE_UNTRUSTED_PROCESS';
export const MIDDLEWARE_SESSION_UNUSABLE = 'MIDDLEWARE_SESSION_UNUSABLE';

/** The official page the Ministry publishes the middleware on. The landing page, never an installer. */
export const MIDDLEWARE_OFFICIAL_URL = 'https://hub.mai.gov.ro/aplicatie-cei';

/**
 * Reads the readiness block out of a health report.
 *
 * Returns `null` for anything it cannot verify — an older service that has never heard of the
 * field, an absent block, a malformed one, or a state outside the closed set.
 *
 * **`null` means "not reported", and not reported never blocks.** That asymmetry is the whole
 * compatibility rule: a service that cannot tell us must leave a working counter working, whereas
 * only a service that positively says "unavailable" may take the action away. Parsing `{}` or a
 * misspelt state as `ready` would be the opposite mistake — claiming knowledge nobody supplied —
 * so it is refused here rather than guessed.
 */
export function parseEidMiddleware(value: unknown): EidMiddlewareReadiness | null {
    if (value === null || typeof value !== 'object') {
        return null;
    }

    const { state, code } = value as { state?: unknown; code?: unknown };

    if (typeof state !== 'string' || !STATES.includes(state as EidMiddlewareState)) {
        return null;
    }

    if (typeof code !== 'string' || code.length === 0) {
        return null;
    }

    return { state: state as EidMiddlewareState, code };
}

/**
 * Whether this verdict positively stops a read.
 *
 * Only `unavailable` does. `unknown` deliberately does not: "could not be determined" is not
 * evidence of absence, and disabling a counter on the service's own uncertainty would be a worse
 * outcome than the gap this check closes. `not-required` and a missing block do not block either.
 */
export function middlewareBlocksRead(readiness: EidMiddlewareReadiness | null): boolean {
    return readiness?.state === 'unavailable';
}

/** Whether the operator should be warned that readiness could not be verified. */
export function middlewareIsUncertain(readiness: EidMiddlewareReadiness | null): boolean {
    return readiness?.state === 'unknown';
}

/**
 * What the Workstation Service page should say about the middleware.
 *
 * A closed set, because the page shows a status chip and one instruction for each: an operator
 * needs to know whether to install something, start something, call IT, or do nothing at all.
 */
export type MiddlewareCardState =
    | 'checking'
    | 'serviceUnreachable'
    | 'notPaired'
    | 'unsupported'
    | 'ready'
    | 'notInstalled'
    | 'notRunning'
    | 'untrusted'
    | 'sessionUnusable'
    | 'unknown';

/**
 * Works out what the page should show.
 *
 * The workstation service is asked first, because nothing can be said about the middleware by a
 * service that is not answering or not paired — reporting "not installed" then would be an
 * invention. `unsupported` is the older-service case: it answered, it is paired, and it simply has
 * no readiness probe, which is a fact about the service rather than about the middleware.
 */
export function deriveMiddlewareCardState(
    status: { state: string } | null,
    readiness: EidMiddlewareReadiness | null,
): MiddlewareCardState {
    if (status === null) {
        return 'checking';
    }

    if (
        status.state === 'unavailable' ||
        status.state === 'unrecognised' ||
        status.state === 'incompatible'
    ) {
        return 'serviceUnreachable';
    }

    if (status.state === 'unpaired') {
        return 'notPaired';
    }

    if (readiness === null) {
        return 'unsupported';
    }

    if (readiness.state === 'ready' || readiness.state === 'not-required') {
        return 'ready';
    }

    if (readiness.state === 'unknown') {
        return 'unknown';
    }

    switch (readiness.code) {
        case MIDDLEWARE_NOT_INSTALLED:
            return 'notInstalled';
        case MIDDLEWARE_NOT_RUNNING:
            return 'notRunning';
        case MIDDLEWARE_UNTRUSTED_PROCESS:
            return 'untrusted';
        case MIDDLEWARE_SESSION_UNUSABLE:
            return 'sessionUnusable';
        default:
            // Positively unavailable for a reason this build does not recognise. Still a refusal,
            // and still worth showing - just without pretending to know which one.
            return 'unknown';
    }
}

/**
 * The i18n key explaining a blocking verdict.
 *
 * Each blocking code gets its own sentence, because each needs a different thing done: install it,
 * start it, look at what is installed, or sign in at the workstation itself. A code this build has
 * never seen still yields a usable sentence rather than a blank.
 */
export function middlewareMessageKey(readiness: EidMiddlewareReadiness): string {
    switch (readiness.code) {
        case MIDDLEWARE_NOT_INSTALLED:
            return 'identityReader.chip.middlewareNotInstalled';
        case MIDDLEWARE_NOT_RUNNING:
            return 'identityReader.chip.middlewareNotRunning';
        case MIDDLEWARE_UNTRUSTED_PROCESS:
            return 'identityReader.chip.middlewareUntrusted';
        case MIDDLEWARE_SESSION_UNUSABLE:
            return 'identityReader.chip.middlewareSessionUnusable';
        default:
            return 'identityReader.chip.middlewareUnavailable';
    }
}
